<script setup lang="ts">
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import { computed, ref, watch } from 'vue';
    import { Option } from '../../autoComplete/types';
    import UserAvatar from '../../UserAvatar.vue';
    import SelectInput from '../../SelectInput.vue';
    import EchoButton from '../../EchoButton.vue';
    import IconButton from '../../IconButton.vue';
    import AutoComplete from '../../autoComplete/AutoComplete.vue';
    import { AccessLevel, UserShare } from '@/js/services/api/shareDashboard/types';
    import {
        useGetUserSharesForDashboard,
        usePutShareDashboardToUsers,
    } from '@/js/services/api/shareDashboard/shareDashboardQueries';
    import { useGetUsersByFilter } from '@/js/services/api/user/userQueries';
    import { useDebouncedRef } from '@/js/composables/useDebouncedRef';
    import { ApiUser } from '@/js/services/api/user/types';
    import LoadingDots from '../../LoadingDots.vue';
    import EchoPopover from '../../EchoPopover.vue';
    import { useGetMyAccount } from '@/js/hooks/user';
    import { useToastStore } from '@/js/stores/snackbar';
    import { useDashboardStore } from '@/js/stores/dashboard';
    import { InfoIcon, PlusIcon, XIcon } from '@lucide/vue';

    const previewAs = defineModel<UserShare | null>('previewAs');

    const props = defineProps<{
        dashboardId: string;
    }>();

    const user = ref<Option | null>(null);
    /** Captured at selection time so it can't desync from a later filter-driven refetch of usersFromDB. */
    const selectedUserDetails = ref<ApiUser | null>(null);
    const autocompleteRef = ref<InstanceType<typeof AutoComplete> | null>(null);

    const selectedSearchFilter = ref('');

    const searchFilter = ref('');

    const debouncedFilter = useDebouncedRef(searchFilter, 500);

    const currentFilter = computed(() => {
        return selectedSearchFilter.value || debouncedFilter.value;
    });

    const {
        data: usersResponse,
        isLoading: isLoadingUsersByFilter,
        isFetching: isFetchingUsersByFilter,
    } = useGetUsersByFilter(currentFilter);

    const usersFromDB = computed(() => usersResponse.value?.users ?? []);

    const { data: sharedUsersResponse, isLoading: isLoadingUserShares } =
        useGetUserSharesForDashboard(props.dashboardId);

    const { data: currentUserAccountData } = useGetMyAccount();
    const toast = useToastStore();

    const { mutateAsync: saveShares, isPending: isSavingShares } = usePutShareDashboardToUsers(
        props.dashboardId
    );

    // Local working copy of shared users (modified before saving)
    const shareWithUsers = ref<UserShare[]>([]);

    const isUserChanged = (userId: string) => {
        const server = sharedUsersResponse.value;
        if (!server) return false;
        const remote = server.find((s) => s.id === userId);
        if (!remote) return true;
        const local = shareWithUsers.value.find((l) => l.id === userId);
        return local?.accessLevel !== remote.accessLevel;
    };

    const hasChanges = computed(() => {
        const server = sharedUsersResponse.value;
        if (!server) return false;
        if (server.length !== shareWithUsers.value.length) return true;
        return shareWithUsers.value.some((user) => isUserChanged(user.id));
    });

    const userOptions = computed<Option[]>(() => {
        return usersFromDB.value
            .map((user) => {
                const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();

                const name = fullName ? `${fullName} (${user.email})` : user.email;

                return {
                    name,
                    value: user.id,
                };
            })
            .filter(
                (option) =>
                    !shareWithUsers.value.find((sharedUser) => sharedUser.id === option.value)
            );
    });

    const canAddUser = computed(() => !!selectedUserDetails.value);

    const dashboard = useDashboardStore();
    const isDashboardOwner = computed(() => dashboard.isDashboardOwner);

    const addUser = () => {
        if (!isDashboardOwner.value) return;

        const userId = user.value?.value;
        const details = selectedUserDetails.value;

        if (!userId || !details) {
            return;
        }

        shareWithUsers.value.unshift({
            id: userId,
            accessLevel: AccessLevel.Viewer,
            name: [details.firstName, details.lastName].filter(Boolean).join(' ').trim(),
            email: details?.email || '',
            role: details?.role || null,
        });

        user.value = null;
        selectedUserDetails.value = null;
        debouncedFilter.value = '';
        autocompleteRef.value?.clearSelected();
    };

    const removeUser = (userId: string) => {
        if (!isDashboardOwner.value) return;

        shareWithUsers.value = shareWithUsers.value.filter((user) => user.id !== userId);

        if (previewAs.value?.id === userId) {
            previewAs.value = null;
        }
    };

    const setAccessLevel = (userId: string, accessLevel: UserShare['accessLevel']) => {
        if (!isDashboardOwner.value) return;

        shareWithUsers.value = shareWithUsers.value.map((user) =>
            user.id === userId ? { ...user, accessLevel } : user
        );
    };

    const save = async () => {
        if (!isDashboardOwner.value) return;

        const usersWithAccessLevels = shareWithUsers.value.map((user) => ({
            id: user.id,
            accessLevel: user.accessLevel,
        }));

        try {
            await saveShares(usersWithAccessLevels);
            toast.addToast('success', __('share-dashboard-users-save-success'));
        } catch (error) {
            console.error('Error saving user shares:', error);
            toast.addToast('error', __('share-dashboard-users-save-error'));
        }
    };

    const isCurrentUser = (user: UserShare) => {
        return currentUserAccountData.value?.id === user.id;
    };

    defineExpose({ hasUnsavedChanges: hasChanges });

    watch(
        () => sharedUsersResponse.value,
        (sharesUsersFromDatabase) => {
            if (sharesUsersFromDatabase) {
                const sortedUsers = [...sharesUsersFromDatabase].sort((a, b) => {
                    const aHasName = !!a.name;
                    const bHasName = !!b.name;

                    if (aHasName !== bHasName) {
                        return aHasName ? -1 : 1;
                    }

                    if (a.name === b.name) {
                        return a.email.localeCompare(b.email);
                    }

                    return a.name.localeCompare(b.name);
                });

                shareWithUsers.value = sortedUsers;
            }
        },
        { immediate: true }
    );
</script>

<template>
    <h2 class="heading-md-light">
        {{ __('add-users') }}
    </h2>
    <p class="text-body">
        {{ __('share-with-user-description') }}
    </p>

    <form
        class="flex flex-col sm:flex-row gap-4 sm:items-center mt-5"
        autocomplete="off"
        @submit.prevent="addUser()"
    >
        <AutoComplete
            ref="autocompleteRef"
            v-model="searchFilter"
            input-id="share-dashboard"
            :options="userOptions"
            :placeholder="__('autocomplete-search-users')"
            :disabled="!isDashboardOwner"
            :loading="
                isFetchingUsersByFilter ||
                isLoadingUsersByFilter ||
                searchFilter !== debouncedFilter
            "
            @select="
                selectedSearchFilter = debouncedFilter;
                user = $event;
                selectedUserDetails = usersFromDB.find((u) => u.id === $event.value) ?? null;
            "
            @clear="
                selectedSearchFilter = '';
                user = null;
                selectedUserDetails = null;
            "
        />
        <div>
            <EchoButton
                :disabled="!isDashboardOwner || !canAddUser"
                type="submit"
                variant="secondary"
            >
                {{ __('add') }}
                <template #icon-right><PlusIcon /></template>
            </EchoButton>
        </div>
    </form>

    <div class="flex-grow flex flex-col mt-6 relative">
        <div v-if="isLoadingUserShares" class="py-10">
            <LoadingDots />
        </div>

        <template v-else>
            <div class="lg:h-0 flex-grow flex flex-col">
                <div class="border-b border-gray-light pb-1.5">
                    <EchoPopover type="text">
                        <template #button="{ anchor, togglePopover, buttonAttrs }">
                            <h3 class="heading-md-light flex items-center">
                                <template v-if="hasChanges">
                                    {{
                                        __('will-be-shared-with-users', {
                                            count: shareWithUsers.length.toString(),
                                        })
                                    }}
                                </template>
                                <template v-else>
                                    {{
                                        __('shared-with-users', {
                                            count: shareWithUsers.length.toString(),
                                        })
                                    }}
                                </template>

                                <div class="-my-1">
                                    <IconButton
                                        :ref="anchor"
                                        area-size="size-8"
                                        :aria-label="__('show-info')"
                                        v-bind="buttonAttrs"
                                        @click="togglePopover"
                                    >
                                        <InfoIcon class="size-5" />
                                    </IconButton>
                                </div>
                            </h3>
                        </template>
                        <template #popover>
                            <p class="mb-2.5">
                                {{ __('share-dashboard-permissions-description') }}
                            </p>
                            <p class="mb-2.5">
                                <strong>
                                    {{ __('share-dashboard-permissions-owner-heading') }}:
                                </strong>
                                {{ __('share-dashboard-permissions-owner-description') }}
                            </p>
                            <p>
                                <strong>
                                    {{ __('share-dashboard-permissions-viewer-heading') }}:
                                </strong>
                                {{ __('share-dashboard-permissions-viewer-description') }}
                            </p>
                        </template>
                    </EchoPopover>
                </div>
                <div class="relative lg:h-0 flex-grow flex flex-col">
                    <div
                        v-if="isSavingShares"
                        class="absolute w-full h-full flex justify-center items-center z-10"
                    >
                        <LoadingDots />
                    </div>
                    <div class="overflow-auto custom-scrollbar">
                        <div
                            v-for="sharedUser in shareWithUsers"
                            :key="'user-' + sharedUser.id"
                            class="grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_1fr_auto_auto] items-center text-body odd:bg-gray-lightest py-2 transition-opacity"
                            :class="{ 'opacity-25 pointer-events-none': isSavingShares }"
                        >
                            <div class="pl-3">
                                <UserAvatar
                                    :label="sharedUser.name || sharedUser.email"
                                    size="md"
                                />
                            </div>
                            <div class="pl-3">
                                <strong class="inline-block pr-3 text-body-bold relative">
                                    {{ sharedUser.name || sharedUser.email }}
                                    <div
                                        v-if="isUserChanged(sharedUser.id)"
                                        class="bg-primary-carrot w-1.5 h-1.5 rounded-full absolute top-1.75 right-0"
                                    />
                                </strong>
                                <span v-if="sharedUser.role" class="block text-caption-bold">
                                    {{ sharedUser.role.name }}
                                </span>
                                <span
                                    class="block text-caption overflow-ellipsis w-32 overflow-hidden"
                                >
                                    {{ sharedUser.email }}
                                </span>
                            </div>
                            <div
                                class="px-3 mt-3 mb-1 ml-12 sm:m-0 order-last col-span-full sm:order-none sm:col-span-1 sm:pr-0"
                            >
                                <SelectInput
                                    size="small"
                                    :model-value="sharedUser.accessLevel"
                                    :disabled="
                                        !isDashboardOwner ||
                                        sharedUser.id === currentUserAccountData?.id
                                    "
                                    @update:model-value="
                                        (val) =>
                                            setAccessLevel(
                                                sharedUser.id,
                                                Number(val) as AccessLevel
                                            )
                                    "
                                >
                                    <option :value="AccessLevel.Viewer">
                                        {{ __('can-view') }}
                                    </option>
                                    <option :value="AccessLevel.Owner">
                                        {{ __('owner') }}
                                    </option>
                                </SelectInput>
                            </div>
                            <!-- 
                            TODO: Enable preview as functionality after backend support is added
                            -->
                            <!-- <div class="pl-3">
                                <EchoButton
                                    size="small"
                                    icon="visibility.svg"
                                    @click="previewAs = sharedUser"
                                    v-if="previewAs?.id !== sharedUser.id"
                                >
                                    {{ __('preview-as') }}
                                </EchoButton>

                                <EchoButton
                                    size="small"
                                    icon="visibility-off.svg"
                                    @click="previewAs = null"
                                    v-else
                                >
                                    {{ __('preview-as') }}
                                </EchoButton>
                            </div> -->
                            <div class="px-3 text-error">
                                <IconButton
                                    area-size="size-5"
                                    :disabled="!isDashboardOwner || isCurrentUser(sharedUser)"
                                    @click="removeUser(sharedUser.id)"
                                >
                                    <XIcon class="size-full" />
                                </IconButton>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="pt-5 border-t border-gray-light flex justify-end">
                    <EchoButton
                        variant="primary"
                        :loading="
                            isSavingShares || (isLoadingUserShares && shareWithUsers.length > 0)
                        "
                        :disabled="!isDashboardOwner || !hasChanges"
                        @click="save()"
                    >
                        {{ __('save') }}
                    </EchoButton>
                </div>
            </div>
        </template>
    </div>
</template>
